import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import PageBackBar from "../components/PageBackBar";
import { useAuth } from "../features/auth/useAuth";
import { RoleLabel } from "../features/auth/types";
import ResetPasswordForm from "../features/settings/components/ResetPasswordForm";
import CreateSuperuserForm from "../features/settings/components/CreateSuperuserForm";
import AdminResetOwnerPasswordForm from "../features/settings/components/AdminResetOwnerPasswordForm";
import "../App.css";
import "./Settings.css";

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ProfileRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="settings-profile-row">
      <div className="settings-profile-row-icon">{icon}</div>
      <div>
        <div className="settings-profile-row-label">{label}</div>
        <div className="settings-profile-row-value">{value}</div>
      </div>
    </div>
  );
}

const Settings = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isSuperuser = user.role === "superuser";

  const profileCard = (
    <article className="settings-panel-card">
      <h2>Profile details</h2>
      <p className="settings-card-sub">Your basic provisioning portal account information.</p>
      <div className="settings-profile-rows">
        <ProfileRow icon={<IconUser />} label="Name" value={user.fullName} />
        <ProfileRow icon={<IconMail />} label="Email" value={user.email} />
        <ProfileRow icon={<IconShield />} label="Access level" value={RoleLabel[user.role] ?? user.role} />
      </div>
      <div className="settings-profile-footer">
        <button type="button" className="settings-btn-outline" disabled title="Profile is read-only in this portal">
          ✎ Edit profile
        </button>
      </div>
    </article>
  );

  return (
    <div className="settings-page">
      <PageBackBar />
      <header className="settings-page-header">
        <h1>Settings</h1>
        <p>Manage your account, security settings, and administrative access.</p>
      </header>

      {isSuperuser ? (
        <div className="settings-card-grid settings-card-grid--quad">
          {profileCard}
          <article className="settings-panel-card">
            <ResetPasswordForm embedded />
          </article>
          <article className="settings-panel-card">
            <CreateSuperuserForm embedded />
          </article>
          <article className="settings-panel-card">
            <AdminResetOwnerPasswordForm embedded />
          </article>
        </div>
      ) : (
        <div className="settings-card-grid settings-card-grid--pair">
          {profileCard}
          <article className="settings-panel-card">
            <ResetPasswordForm embedded />
          </article>
        </div>
      )}
    </div>
  );
};

export default Settings;
